// eslint-disable-next-line no-restricted-imports
import { useFlag } from '@unleash/proxy-client-react'
import { useMemo } from 'react'

import {
  SupportedCountry,
  SupportedLanguage,
} from '~shared-components/utils/enums'

export enum Flag {
  AdditionalItems = 'additionalItems',
  EditUserProfilePerCountry = 'editUserProfilePerCountry',
  EnabledLanguages = 'enabledLanguages',
  ExportInvoicesAndOrders = 'exportInvoicesAndOrders',
  ExternalUserAdminPerCountry = 'externalUserAdminPerCountry',
  GlobalSearchPerCountry = 'globalSearchPerCountry',
  InvoicesPageForCsrUiPerCountry = 'invoicesPageForCsrUiPerCountry',
  InvoicesPagePerCountry = 'invoicesPagePerCountry',
  MaintenancePage = 'maintenancePage',
  OrderHistoryLogPerCountry = 'orderHistoryLogPerCountry',
  OrdersPageForCsrUiPerCountry = 'ordersPageForCsrUiPerCountry',
  PayOnline = 'payOnline',
  ProductsPageForCsrUiPerCountry = 'productsPageForCsrUiPerCountry',
  QuotesPagePerCountry = 'quotesPagePerCountry',
  RequestOrderCancellation = 'requestOrderCancellation',
  RequestOrderChanges = 'requestOrderChanges',
  UploadUserDocuments = 'uploadUserDocuments',
}

export type FlagValues = {
  [Flag.EditUserProfilePerCountry]: Record<SupportedCountry, boolean>
  [Flag.EnabledLanguages]: Record<SupportedLanguage, number>
  [Flag.ExternalUserAdminPerCountry]: Record<SupportedCountry, boolean>
  [Flag.GlobalSearchPerCountry]: Record<SupportedCountry, boolean>
  [Flag.InvoicesPageForCsrUiPerCountry]: Record<SupportedCountry, boolean>
  [Flag.InvoicesPagePerCountry]: Record<SupportedCountry, boolean>
  [Flag.OrderHistoryLogPerCountry]: Record<SupportedCountry, boolean>
  [Flag.OrdersPageForCsrUiPerCountry]: Record<SupportedCountry, boolean>
  [Flag.ProductsPageForCsrUiPerCountry]: Record<SupportedCountry, boolean>
  [Flag.QuotesPagePerCountry]: Record<SupportedCountry, boolean>
} & { [key: string]: boolean }

/**
 * The Unleash flag key, spelled exactly as it is in Unleash.
 *
 * LaunchDarkly camelCased flag keys before handing them to the client, which is
 * why `Flag.MaintenancePage` reads `maintenancePage`. Unleash does no such
 * transformation, so the kebab-case key has to be used verbatim.
 */
export const MAINTENANCE_PAGE_FLAG = 'maintenance-page'

/**
 * `maintenancePage` is the only flag still evaluated at runtime, so it is the
 * only one this hook serves. It is the operational kill switch and has to stay
 * flippable without a deploy, which is why it did not become a constant.
 *
 * Every other flag is a compile-time constant in `frozenFlags.ts`, imported
 * directly by the code that reads it. That makes this hook the single place that
 * reads a flag through a provider.
 *
 * Callers must be inside a `FlagProvider` — `useFlag` throws without one. In
 * practice that means below `MaintenanceGate`, which is the only consumer.
 */
export const useFlags = (): { maintenancePage: boolean } => {
  const maintenancePage = useFlag(MAINTENANCE_PAGE_FLAG)

  return useMemo(() => ({ maintenancePage }), [maintenancePage])
}
